import { Input, Text } from '@chakra-ui/react';
import { IconCheckmark2 } from 'central-icons/IconCheckmark2';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';

import { FILTER_SET_NAME_MAX_LENGTH } from '@revelio/iso-utility';
import {
  Button,
  Divider,
  FilterTag,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  NavigationButton,
  Popover,
  IconButton as UiIconButton,
  Input as UiInput,
  Text as UiText,
} from '@revelio/ui';

import type { Option } from '../../filters/types/filters';
import {
  describeFilterEntries,
  type FilterLine,
} from '../../filters/utils/describeFilterMap';
import { decodeSavedViewFilters } from '../../filters/utils/savedViewUrl';
import { useToast } from '../../hooks/useToast';
import styles from './SavedViewsList.module.css';
import layoutStyles from './saved-views-list.module.css';
import { setSavedViewPreview } from './savedViewPreviewStore';
import { type ContentMatch, groupSearchResults } from './savedViewSearch';
import { type SavedView, useSavedViews } from './useSavedViews';

export type SavedViewOption = Option<SavedView> & {
  entity: SavedView;
};

interface SetsListProps {
  options: SavedViewOption[];
  onApply: (opt: SavedViewOption) => void;
  onUpdated?: (opt: SavedView) => void;
  onDeleted?: (opt: SavedView) => void;
  onOverwritten?: (opt: SavedView) => void;
  emptySlot?: React.ReactNode;
  deleteSlot?: (opt: SavedViewOption) => React.ReactNode;
  overwriteSlot?: (opt: SavedViewOption) => React.ReactNode;
  /** What an overwrite stores; without it the menu offers no overwrite. */
  currentUrl?: () => string;
  /** False while the page has nothing worth saving. */
  canOverwrite?: boolean;
  /**
   * Whether a set's stored url is a filter map the page can preview and
   * describe. Economic Impact sets encode scenarios, not filters.
   */
  canPreview?: boolean;
}

/** A pending delete or overwrite, shown in place of the list until answered. */
type Confirmation = { kind: 'delete' | 'overwrite'; id: string };

const describeSet = (url: string): FilterLine[] | null => {
  const entries = decodeSavedViewFilters(url);
  return entries ? describeFilterEntries(entries) : null;
};

export const SavedViewsList = ({
  options,
  onApply,
  onUpdated,
  onDeleted,
  onOverwritten,
  emptySlot,
  deleteSlot,
  overwriteSlot,
  currentUrl,
  canOverwrite = true,
  canPreview = false,
}: SetsListProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirmation | null>(null);
  const [confirmingBusy, setConfirmingBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Hovering a row paints its filters into the page as a preview; hovering the
  // info icon by its name opens a card with every value. The preview outlives
  // the row by a moment, long enough for the pointer to cross the gap between
  // the icon and its card without the page flashing.
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPreviewTimer = () => {
    if (previewTimer.current !== null) clearTimeout(previewTimer.current);
    previewTimer.current = null;
  };
  const releasePreviewSoon = () => {
    clearPreviewTimer();
    previewTimer.current = setTimeout(() => setSavedViewPreview(null), 200);
  };
  const endPreview = () => {
    clearPreviewTimer();
    setSavedViewPreview(null);
  };
  useEffect(
    () => () => {
      if (previewTimer.current !== null) clearTimeout(previewTimer.current);
      setSavedViewPreview(null);
    },
    []
  );
  const linesById = useMemo(
    () =>
      new Map(
        canPreview
          ? options.map((opt) => [opt.value, describeSet(opt.entity.url)])
          : []
      ),
    [options, canPreview]
  );
  // Shows the set on the page and keeps it there until released: the row, its
  // card and a delete or overwrite confirmation all hold the same preview.
  const holdPreview = (opt: SavedViewOption) => {
    clearPreviewTimer();
    const lines = linesById.get(opt.value);
    setSavedViewPreview(lines ? { name: opt.label, lines } : null);
  };

  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const toast = useToast();

  const { updateView, deleteView, canManageViews } = useSavedViews();

  // A search finds a set by its name or by a value it holds, sets matched
  // on the same filter together; a set found by its contents says which
  // value matched.
  const query = search.trim().toLowerCase();
  const results = groupSearchResults(
    options.map((opt) => ({
      item: opt,
      name: opt.label,
      lines: linesById.get(opt.value) ?? null,
    })),
    query
  );

  // When there are no options at all (before any search), defer to provided emptySlot logic
  if (options.length === 0) {
    return (
      emptySlot ?? (
        <Text fontSize="sm" color="gray.500">
          No saved views found
        </Text>
      )
    );
  }

  // Looked up in every option, not the search results: the search stays
  // editable under a confirmation, and must not hide it with its preview on.
  const confirmingOpt = confirming
    ? options.find((o) => o.value === confirming.id)
    : undefined;

  const notify = (title: string, status: 'success' | 'error') =>
    toast({ title, status, position: 'top-right', isClosable: true });

  const handleSave = (opt: SavedViewOption, name: string) => {
    const safeName = DOMPurify.sanitize(name, { ALLOWED_TAGS: [] });

    // no change, do nothing
    if (opt.entity.name === name) {
      setEditingId(null);
      return;
    }

    // empty name, do nothing
    if (safeName.length === 0) {
      setEditingId(null);
      return;
    }

    setSavingId(opt.value);
    updateView({ id: opt.value, name: safeName })
      .then((res) => {
        if (res.error) {
          notify(
            res.error.isConflict
              ? 'A view with this name already exists'
              : 'Could not rename view',
            'error'
          );
        } else {
          notify('View renamed', 'success');
          onUpdated?.({ ...opt.entity, name: safeName });
        }
      })
      .finally(() => {
        setEditingId(null);
        setSavingId(null);
      });
  };

  const askToConfirm = (kind: Confirmation['kind'], opt: SavedViewOption) => {
    setConfirming({ kind, id: opt.value });
    holdPreview(opt);
  };

  const closeConfirmation = () => {
    setConfirming(null);
    endPreview();
  };

  const handleDelete = (opt: SavedViewOption) => {
    setConfirmingBusy(true);
    deleteView(opt.value)
      .then((res) => {
        if (res.error) {
          notify('Could not delete view', 'error');
        } else {
          notify('View deleted', 'success');
          onDeleted?.(opt.entity);
        }
      })
      .finally(() => {
        setConfirmingBusy(false);
        closeConfirmation();
      });
  };

  // An overwrite keeps the name and replaces what the set applies with the
  // page as it is now.
  const handleOverwrite = (opt: SavedViewOption) => {
    if (!currentUrl) return;
    const url = currentUrl();
    setConfirmingBusy(true);
    updateView({ id: opt.value, url })
      .then((res) => {
        if (res.error) {
          notify('Could not overwrite view', 'error');
        } else {
          notify('View overwritten', 'success');
          onOverwritten?.({ ...opt.entity, url });
        }
      })
      .finally(() => {
        setConfirmingBusy(false);
        closeConfirmation();
      });
  };

  const confirmDialog = ({ kind, id }: Confirmation, opt: SavedViewOption) => {
    const isDelete = kind === 'delete';
    const slot = isDelete ? deleteSlot : overwriteSlot;
    return (
      <>
        {slot ? (
          slot(opt)
        ) : (
          <UiText fontSize="sm">
            Are you sure you want to {kind} <b>{opt.label}</b> saved view?
          </UiText>
        )}
        <div className={layoutStyles.confirmActions}>
          <Button
            size="sm"
            variant="ghost"
            color="secondary"
            onClick={closeConfirmation}
            disabled={confirmingBusy}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="destructive"
            loading={confirmingBusy}
            onClick={() =>
              isDelete ? handleDelete(opt) : handleOverwrite(opt)
            }
            data-testid={`saved-view-${kind}-confirm-${id}`}
          >
            {isDelete ? 'Delete' : 'Overwrite'}
          </Button>
        </div>
      </>
    );
  };

  const editDialog = (opt: SavedViewOption) => (
    <div className={layoutStyles.editRow} key={opt.value}>
      <Input
        data-testid={`saved-view-item-name-input-${opt.value}`}
        ref={inputRef}
        size="sm"
        value={editingName}
        variant="flushed"
        onChange={(e) => setEditingName(e.target.value)}
        flex={1}
        px={2.5}
        maxLength={FILTER_SET_NAME_MAX_LENGTH}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSave(opt, editingName);
          } else if (e.key === 'Escape') {
            setEditingId(null);
          }
        }}
      />
      <UiIconButton
        data-testid={`saved-view-item-save-${opt.value}`}
        disabled={editingName.trim().length === 0}
        size="sm"
        variant="ghost"
        shape="squared"
        aria-label="Save"
        icon={<IconCheckmark2 size={14} />}
        loading={savingId === opt.value}
        onClick={() => handleSave(opt, editingName)}
      />
    </div>
  );

  const highlight = (label: string) => {
    if (!query) return label;
    const idx = label.toLowerCase().indexOf(query);
    if (idx === -1) return label;
    const before = label.slice(0, idx);
    const match = label.slice(idx, idx + query.length);
    const after = label.slice(idx + query.length);
    return (
      <>
        {before}
        <mark>{match}</mark>
        {after}
      </>
    );
  };

  const detailsCard = (opt: SavedViewOption) => {
    const lines = linesById.get(opt.value) ?? null;
    return (
      <>
        <UiText
          fontSize="sm"
          weight="semibold"
          className={styles.previewTitle}
          data-testid={`saved-view-item-details-${opt.value}`}
        >
          {opt.label}
        </UiText>
        {!lines && (
          <UiText fontSize="x-sm">
            Saved in an older format; details unavailable
          </UiText>
        )}
        {lines && (
          <div className={styles.previewLines}>
            {lines.map((line) => (
              <div key={line.key} className={styles.previewLine}>
                <span className={styles.previewLabel}>
                  {line.operator
                    ? `${line.label} · ${line.operator}`
                    : line.label}
                </span>
                <div className={styles.chipRow}>
                  {line.values.map((value, index) => (
                    <FilterTag
                      key={`${index}-${value}`}
                      className={styles.detailChip}
                      title={value}
                      data-testid="saved-view-detail-chip"
                    >
                      {value}
                    </FilterTag>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const item = (opt: SavedViewOption, contentMatch: ContentMatch | null) => {
    const button = (
      <NavigationButton
        data-testid={`saved-view-item-${opt.value}`}
        key={opt.value}
        className={styles.rowButton}
        isSelected={false}
        size="sm"
        onClick={() => onApply(opt)}
        alwaysShowSlot={menuOpenId === opt.value}
        slot={
          canManageViews && (
            <Menu
              open={menuOpenId === opt.value}
              onOpenChange={(open) => setMenuOpenId(open ? opt.value : null)}
            >
              <MenuButton>
                <UiIconButton
                  data-testid={`saved-view-item-actions-${opt.value}`}
                  icon={<IconDotGrid1x3Vertical size={14} />}
                  aria-label="Actions"
                  size="sm"
                  variant="ghost"
                  shape="squared"
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuButton>
              <MenuList portal={false} align="end" minWidth={96}>
                <MenuItem
                  data-testid={`saved-view-item-rename-${opt.value}`}
                  onClick={() => {
                    setEditingId(opt.value);
                    setEditingName(opt.label);
                    setMenuOpenId(null);
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 0);
                  }}
                >
                  Rename
                </MenuItem>
                {currentUrl && (
                  <MenuItem
                    data-testid={`saved-view-item-overwrite-${opt.value}`}
                    disabled={!canOverwrite}
                    onClick={() => askToConfirm('overwrite', opt)}
                  >
                    Overwrite
                  </MenuItem>
                )}
                <MenuItem
                  data-testid={`saved-view-item-delete-${opt.value}`}
                  onClick={() => askToConfirm('delete', opt)}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          )
        }
      >
        <span className={styles.nameLine}>
          {/* One line, cut with an ellipsis: the whole name is in the
              details card. */}
          <span className={styles.rowName}>{highlight(opt.label)}</span>
          {canPreview && (
            // The card is the dashboard's hover tooltip: it opens below the
            // icon with an arrow, like a company's info in the filter tree.
            <Popover
              openOnHover
              closeDelay={100}
              placement="bottom"
              arrow
              sideOffset={12}
              className={styles.previewCard}
              anchor={
                <span
                  data-testid={`saved-view-item-details-icon-${opt.value}`}
                  className={styles.infoIcon}
                  // Decorative: the row's name must stay the set's name alone.
                  aria-hidden
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconCircleInfo size={14} />
                </span>
              }
            >
              {/* A click on the card must not apply the set the way one on
                  the row does; the preview stays while the card is hovered. */}
              <div
                className={styles.previewContent}
                onClick={(e) => e.stopPropagation()}
                onPointerEnter={() => holdPreview(opt)}
                onPointerLeave={releasePreviewSoon}
              >
                {detailsCard(opt)}
              </div>
            </Popover>
          )}
        </span>
        {contentMatch && (
          <span
            className={styles.matchReason}
            data-testid={`saved-view-item-match-${opt.value}`}
          >
            {contentMatch.label}: {highlight(contentMatch.value)}
          </span>
        )}
      </NavigationButton>
    );
    if (!canPreview) return button;
    // The wrapper covers the row and its action slot together.
    return (
      <div
        key={opt.value}
        className={styles.row}
        onPointerEnter={() => holdPreview(opt)}
        onPointerLeave={releasePreviewSoon}
      >
        {button}
      </div>
    );
  };

  return (
    <div className={layoutStyles.list}>
      <UiInput
        ref={searchInputRef}
        size="sm"
        placeholder="Search saved views..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        endSlot={
          search && (
            <UiIconButton
              className={styles.clearSearchButton}
              aria-label="Clear search"
              size="sm"
              variant="ghost"
              shape="squared"
              icon={<IconCrossLarge size={12} />}
              onClick={() => {
                setSearch('');
                searchInputRef.current?.focus();
              }}
            />
          )
        }
      />
      {/* Edge to edge under the search, as the Pages panel draws its line. */}
      <Divider className={styles.searchDivider} />
      {confirming && confirmingOpt ? (
        confirmDialog(confirming, confirmingOpt)
      ) : (
        <>
          {results.map(({ item: opt, match }) =>
            editingId === opt.value ? editDialog(opt) : item(opt, match)
          )}
          {results.length === 0 && (
            <Text fontSize="sm" color="gray.500" textAlign="center" p={4}>
              No views found
            </Text>
          )}
        </>
      )}
    </div>
  );
};
